import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { CpclLabelBytes } from '@tookdee/thermal-printer-lib-js';
import { firstValueFrom, timeout } from 'rxjs';

import { RtcTagPrinter, buildRtcMobileProductTagLabelsJson } from './rtc-tag-printer';
import { CpclGeometry, MEASURED_GEOMETRY } from '../models/cpcl-geometry';
import { RtcTagPrintInfoResponse } from '../models/product-tag.model';

// relay ตั้ง TCP timeout ไปหาเครื่องพิมพ์ไว้ 3 วินาที ฝั่ง HTTP จึงเผื่อไว้เหนือกว่านั้นพอสมควร
const RELAY_TIMEOUT_MS = 10000;

// หน่วงระหว่างใบ ไม่หน่วงก่อนใบแรก — เครื่องพิมพ์รับไม่ทันถ้ายิงติดกัน
const DELAY_BETWEEN_LABELS_MS = 200;

/**
 * ยิงงานพิมพ์ป้าย RTC ผ่าน Relay ไปเข้า mobile printer (toggle Short-Lived Product = ON)
 *
 * ต่างจากทาง WebUSB ตรงที่ **Angular เป็นเจ้าของ transport เอง** — library คืน byte มาเป็น array
 * หนึ่ง entry คือหนึ่งป้าย เราต้องวนส่งทีละใบ หน่วงเอง และปล่อย connection เอง
 *
 * ห้ามส่ง header `X-Printer-Host` / `X-Printer-Port` — relay รองรับอยู่ แต่พอไม่มี header มันจะใช้
 * เครื่องที่ตั้งไว้ใน `relay.properties` ของตัวเอง ซึ่งเป็นสิ่งที่เราต้องการ
 */
@Injectable({ providedIn: 'root' })
export class RelayPrinterService implements RtcTagPrinter {
  readonly connected = signal(false);

  /** เริ่มจากค่าที่วัดไว้ แล้วให้หน้าคาลิเบรตเขียนทับได้ */
  readonly geometry = signal<CpclGeometry>(MEASURED_GEOMETRY);

  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.relayPrinterUrl;

  async requestAndConnect(): Promise<void> {
    await this.post('/connect');
    this.connected.set(true);
  }

  async printRtcProductTags(products: RtcTagPrintInfoResponse[]): Promise<void> {
    const labels = await this.buildLabels(buildRtcMobileProductTagLabelsJson(products));

    for (const [index, label] of labels.entries()) {
      if (index > 0) {
        await delay(DELAY_BETWEEN_LABELS_MS);
      }
      // หนึ่งป้าย หนึ่ง request — ห้าม concat หลายใบ เพราะสิ่งที่ถึงเครื่องพิมพ์จะเปลี่ยน
      await this.post('/print', label);
    }
  }

  /**
   * ต้องเรียกทุกครั้ง เครื่องพิมพ์ให้บริการทีละราย ถือค้างคือบล็อกทุกคน — relay ตอบ `not connected`
   * มาก็ถือว่าสำเร็จ
   */
  async disconnect(): Promise<void> {
    await this.post('/disconnect');
    this.connected.set(false);
  }

  /** ถามว่า relay ยังถือ socket อยู่ไหม — `disconnected` คือสถานะปกติของหน้าจอ ไม่ใช่ error */
  async status(): Promise<string> {
    return firstValueFrom(
      this.http.get(this.baseUrl + '/status', { responseType: 'text' }).pipe(timeout(RELAY_TIMEOUT_MS))
    ).catch(toRelayError);
  }

  /**
   * หนึ่ง entry = หนึ่งป้าย
   *
   * geometry ทั้ง 5 ค่าเป็น optional parameter ของ library แต่**ต้องส่งครบเสมอ** — default ที่ฝังไว้
   * (384 / 168 / 88 / 0 / 40) เป็นค่าเก่า ถ้าเผลอไม่ส่งจะได้ค่านั้นโดยไม่มีสัญญาณเตือน
   */
  private buildLabels(json: string): Promise<Uint8Array[]> {
    const g = this.geometry();
    return new CpclLabelBytes().build(json, g.widthDots, g.heightDots, g.xDots, g.yDots, g.bottomMarginDots);
  }

  // HttpClient ปล่อยผ่านดิบเฉพาะ string/ArrayBuffer/Blob/FormData — Uint8Array โดน JSON.stringify
  // Blob ที่ไม่ใส่ type ทำให้ไม่มี header Content-Type เหมือน fetch จึงยังเป็น CORS simple request
  private post(path: string, body?: Uint8Array): Promise<string> {
    const payload = body ? new Blob([body]) : '';
    return firstValueFrom(
      this.http.post(this.baseUrl + path, payload, { responseType: 'text' }).pipe(timeout(RELAY_TIMEOUT_MS))
    ).catch(toRelayError);
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * error ของทางนี้มาสองชั้น ชั้นแรกคือยิงไม่ถึง relay เลย (`status 0`) ซึ่ง**แยกไม่ได้**ว่า relay ไม่ได้รัน
 * หรือรันอยู่แต่ปฏิเสธ origin ของเรา — ต้องบอกผู้ใช้ทั้งสองสาเหตุ อย่าเดาให้
 * ชั้นที่สองคือ relay ตอบมาแต่ไม่ ok — body เป็น plain text เอาไปแสดงต่อได้เลย
 */
function toRelayError(error: unknown): never {
  if (error instanceof HttpErrorResponse) {
    throw new RelayPrinterError(error.status, typeof error.error === 'string' ? error.error : error.message);
  }
  throw error;
}

export class RelayPrinterError extends Error {
  /** `0` = ยิงไม่ถึง relay (relay ไม่ได้รัน หรือ origin ไม่ผ่าน CORS) */
  constructor(
    readonly status: number,
    readonly body: string
  ) {
    super(`relay ${status}: ${body}`);
  }
}
